import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, List, ListItemButton, ListItemText, Stack, TextField, Typography } from '@mui/material';
import { CircleMarker, MapContainer, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { http } from '../../core/http/client';
import type { Values } from './hubApi';
import { errorMessage } from './HubUi';

interface Location { id: string; title: string; label: string; latitude: number; longitude: number; city?: string | null }
const layers = {
  streets: { label: 'Streets', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 },
  terrain: { label: 'Terrain', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: 'Map data &copy; OpenStreetMap contributors, SRTM | Map style &copy; OpenTopoMap', maxZoom: 17 },
  satellite: { label: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri', maxZoom: 19 },
};

function MapControls({ latitude, longitude, onPick, disabled }: { latitude: number; longitude: number; onPick: (lat: number, lng: number) => void; disabled: boolean }): null {
  const map = useMap();
  useEffect(() => { map.setView([latitude, longitude], map.getZoom()); }, [latitude, longitude, map]);
  useEffect(() => {
    const observer = new ResizeObserver(() => map.invalidateSize());
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [map]);
  useMapEvents({ click: event => { if (!disabled) onPick(event.latlng.lat, event.latlng.lng); } });
  return null;
}

export function ProfileLocationSearch({ value, onChange, disabled, mode = 'business' }: {
  value: Values; onChange: (value: Values) => void; disabled: boolean; mode?: 'business' | 'job';
}) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Location | null>(null);
  const [results, setResults] = useState<Location[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searched, setSearched] = useState(false);
  const [layer, setLayer] = useState<keyof typeof layers>('streets');
  const request = useRef<AbortController | null>(null);
  const reverseRequest = useRef<AbortController | null>(null);
  const currentValue = useRef(value);
  currentValue.current = value;
  useEffect(() => () => { request.current?.abort(); reverseRequest.current?.abort(); }, []);
  const latitude = typeof value.latitude === 'number' ? value.latitude : undefined;
  const longitude = typeof value.longitude === 'number' ? value.longitude : undefined;
  const hasLocation = latitude !== undefined && longitude !== undefined;
  const pick = async (item: Location) => {
    reverseRequest.current?.abort();
    let location = item;
    if (mode === 'job' && !item.city) {
      const controller = new AbortController();
      reverseRequest.current = controller;
      setLoading(true); setError(null);
      try {
        const response = await http().get<Location>('/jobs/locations/reverse', {
          params: { latitude: item.latitude, longitude: item.longitude }, signal: controller.signal, skipErrorToast: true,
        });
        if (controller.signal.aborted) return;
        location = response.data;
      } catch (err) {
        if (!controller.signal.aborted) setError(errorMessage(err));
        return;
      } finally { if (!controller.signal.aborted) setLoading(false); }
    }
    if (mode === 'job' && !location.city) {
      setError('The selected location has no city. Choose another place on the map.');
      return;
    }
    setSelected(location);
    onChange({ ...currentValue.current, latitude: location.latitude, longitude: location.longitude,
      ...(mode === 'job' ? { addressLine: location.label.slice(0, 300), city: location.city?.slice(0, 100) } : {}),
    });
  };
  const search = async () => {
    if (disabled || loading || query.trim().length < 3) return;
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setLoading(true); setError(null); setResults([]); setSearched(false);
    try {
      const { data } = await http().get<Location[]>(mode === 'job' ? '/jobs/locations/search' : '/organization-profiles/locations/search', {
        params: { q: query.trim() }, signal: controller.signal, skipErrorToast: true,
      });
      if (!controller.signal.aborted) { setResults(data); setSearched(true); }
    } catch (err) { if (!controller.signal.aborted) setError(errorMessage(err)); }
    finally { if (!controller.signal.aborted) setLoading(false); }
  };
  const mapLatitude = latitude ?? results[0]?.latitude ?? 41.3231;
  const mapLongitude = longitude ?? results[0]?.longitude ?? 19.4414;
  return <Box sx={{ mt: mode === 'job' ? 1.5 : 3 }}>
    {mode === 'business' && <Typography fontWeight={600} sx={{ mb: 1 }}>Business location</Typography>}
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField fullWidth disabled={disabled} label="Search location" placeholder="Search an address or business" value={query}
        onChange={event => setQuery(event.target.value)}
        onKeyDown={event => { if (event.key === 'Enter') { event.preventDefault(); void search(); } }} />
      <Button type="button" variant="outlined" disabled={disabled || loading || query.trim().length < 3} onClick={() => void search()}>
        {loading ? <CircularProgress size={20} /> : 'Search'}
      </Button>
    </Stack>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Search for a place or click on the map to choose the {mode === 'job' ? 'job location' : 'business location'}.</Typography>
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {results.length > 0 && <List dense aria-label="Location search results" sx={{ maxHeight: 200, overflow: 'auto' }}>
      {results.map(item => <ListItemButton key={item.id} disabled={disabled || loading} selected={selected?.id === item.id} onClick={() => void pick(item)}>
        <ListItemText primary={item.title} secondary={item.label} />
      </ListItemButton>)}
    </List>}
    {searched && results.length === 0 && <Typography sx={{ mt: 1 }}>No locations found. Try another address or choose a point on the map.</Typography>}
    <Stack direction="row" spacing={1} sx={{ my: 1 }}>
      {(Object.keys(layers) as (keyof typeof layers)[]).map(key => <Button key={key} size="small" variant={layer === key ? 'contained' : 'outlined'} onClick={() => setLayer(key)}>{layers[key].label}</Button>)}
    </Stack>
    <Box sx={{ height: mode === 'job' ? 260 : 320, borderRadius: 2, overflow: 'hidden', position: 'relative', isolation: 'isolate' }}>
      <MapContainer center={[mapLatitude, mapLongitude]} zoom={13} minZoom={3} maxZoom={layers[layer].maxZoom} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer key={layer} url={layers[layer].url} attribution={layers[layer].attribution} maxZoom={layers[layer].maxZoom} />
        <MapControls latitude={mapLatitude} longitude={mapLongitude} disabled={disabled || loading} onPick={(lat, lng) => void pick({ id: 'map-point', title: 'Selected map point', label: 'Selected map point', latitude: lat, longitude: lng })} />
        {hasLocation && <CircleMarker center={[latitude, longitude]} radius={9} pathOptions={{ color: '#fff', weight: 3, fillColor: '#2563eb', fillOpacity: 1 }} />}
      </MapContainer>
    </Box>
    {hasLocation && <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">{selected ? `Selected: ${selected.label}` : 'A location is already saved. Search or click the map to change it.'}</Typography>
      <Button size="small" disabled={disabled} onClick={() => { setSelected(null); onChange({ ...value, latitude: null, longitude: null, ...(mode === 'job' ? { city: '', addressLine: '' } : {}) }); }}>Remove location</Button>
    </Stack>}
    <Typography variant="caption" color="text.secondary">Search data &copy; OpenStreetMap contributors</Typography>
  </Box>;
}
